//#region Imports
import { SETTINGS_AUTH_ME_URL, UserDetailsRecord, createSingleEventEmitter } from "@anydo/core";
import type { IReadonlyUserDetailsRecord } from "@anydo/interfaces";
import { Draft, produce } from "@anydo/vendor/immer";
import { fetchAuth } from "./fetchAuth";
import { createAuthHeaders } from "./utils/createAuthHeaders";
//#endregion

//#region Types
type SupportedKeyT = Extract<
  keyof IReadonlyUserDetailsRecord,
  | "name"
  | "profilePicture"
  | "completedCounter"
  | "firstDayOfWeek"
  | "webConfig"
  | "myDayConfig"
  | "myDaySuggestionFilters"
>;

interface IUserDetailsUpdateable extends Pick<IReadonlyUserDetailsRecord, SupportedKeyT> {}

interface IQueueItem {
  result: IReadonlyUserDetailsRecord;
  updates: Partial<IUserDetailsUpdateable>;
  status: "idle" | "loading" | "error";
}
//#endregion

//#region Helpers
function defer<T>() {
  let resolve!: (value: T) => void;
  let reject!: (error: any) => void;
  const promise = new Promise<T>((resolve_, reject_) => {
    resolve = resolve_;
    reject = reject_;
  });

  return { promise, resolve, reject };
}

async function updateUserDetails(authToken: string, updates: Partial<IUserDetailsUpdateable>) {
  await fetchAuth(SETTINGS_AUTH_ME_URL, {
    method: "PUT",
    headers: createAuthHeaders(authToken),
    body: JSON.stringify(updates),
  });
}
//#endregion

//#region Queue helpers
const createResult = produce(
  (
    draft: Draft<IReadonlyUserDetailsRecord>,
    {
      name,
      profilePicture,
      completedCounter,
      firstDayOfWeek,
      webConfig,
      myDayConfig,
      myDaySuggestionFilters,
    }: Partial<IUserDetailsUpdateable>,
  ) => {
    if (name !== undefined) UserDetailsRecord.set(draft, "name", name);
    if (profilePicture !== undefined) UserDetailsRecord.set(draft, "profilePicture", profilePicture);
    if (completedCounter !== undefined) UserDetailsRecord.set(draft, "completedCounter", completedCounter);
    if (firstDayOfWeek !== undefined) UserDetailsRecord.set(draft, "firstDayOfWeek", firstDayOfWeek);
    if (webConfig !== undefined) UserDetailsRecord.set(draft, "webConfig", webConfig);
    if (myDayConfig !== undefined) UserDetailsRecord.set(draft, "myDayConfig", myDayConfig);
    if (myDaySuggestionFilters !== undefined)
      UserDetailsRecord.set(draft, "myDaySuggestionFilters", myDaySuggestionFilters);
  },
);

function createQueueItem(
  userDetails: IReadonlyUserDetailsRecord,
  updates: Partial<IUserDetailsUpdateable>,
): IQueueItem {
  return {
    status: "idle",
    updates,
    result: createResult(userDetails, updates),
  };
}

function getLatest<T>(newer: T | undefined, fallback: T | undefined): T | undefined {
  return newer === undefined ? fallback : newer;
}

function flattenQueueToItem(queue: IQueueItem[], userDetails: IReadonlyUserDetailsRecord): IQueueItem | undefined {
  if (queue.length === 0) return undefined;
  if (queue.length === 1) return queue[0];

  let updates;

  for (let item of queue) {
    if (updates === undefined) {
      updates = item.updates;
    } else {
      updates = {
        completedCounter: getLatest(item.updates.completedCounter, updates.completedCounter),
        firstDayOfWeek: getLatest(item.updates.firstDayOfWeek, updates.firstDayOfWeek),
        name: getLatest(item.updates.name, updates.name),
        profilePicture: getLatest(item.updates.profilePicture, updates.profilePicture),
        webConfig: getLatest(item.updates.webConfig, updates.webConfig),
        myDayConfig: getLatest(item.updates.myDayConfig, updates.myDayConfig),
        myDaySuggestionFilters: getLatest(item.updates.myDaySuggestionFilters, updates.myDaySuggestionFilters),
      };
    }
  }

  if (updates === undefined) return;

  return {
    status: "idle",
    updates,
    result: createResult(userDetails, updates),
  };
}

function flattenQueue(queue: IQueueItem[], userDetails: IReadonlyUserDetailsRecord): void {
  const item = flattenQueueToItem(queue, userDetails);

  if (item !== undefined) {
    queue.length = 0;
    queue.push(item);
  }
}
//#endregion

export class AuthUserDetailsUpdateQueue {
  //#region Setup
  private readonly _queue: IQueueItem[] = [];
  private readonly _changeEmitter = createSingleEventEmitter();
  private readonly _updateServer;
  private _userDetails;
  private _currentUpdatePromise: Promise<void> | null = null;

  constructor(userDetails: IReadonlyUserDetailsRecord, updateServer = updateUserDetails) {
    this._userDetails = userDetails;
    this._updateServer = updateServer;
  }

  destroy(): void {
    this._queue.length = 0;
    this._changeEmitter.off();
  }
  //#endregion

  private _emitChangeWithSafety() {
    try {
      // Notify for upcomming change
      this._changeEmitter.emit();
    } catch (error) {
      // If the emit caused error, we empty queue since we don't to create stale errors
      this._queue.length = 0;
      throw error;
    }
  }

  readonly onChange = this._changeEmitter.on;

  getStatus(): "idle" | "loading" | "error" {
    if (this._queue.length === 0) {
      return "idle";
    } else if (this._queue.some((item) => item.status === "error")) {
      return "error";
    } else {
      return "loading";
    }
  }

  isCommitted(): boolean {
    return this.getStatus() === "idle";
  }

  isLoading(): boolean {
    return this.getStatus() === "loading";
  }

  hasError(): boolean {
    return this.getStatus() === "error";
  }

  getUserDetails(): IReadonlyUserDetailsRecord {
    let lastItem;

    for (let item of this._queue) {
      lastItem = item;
    }

    return lastItem === undefined ? this._userDetails : lastItem.result;
  }

  async update(
    authToken: string,
    {
      name,
      profilePicture,
      completedCounter,
      firstDayOfWeek,
      webConfig,
      myDayConfig,
      myDaySuggestionFilters,
    }: Partial<IUserDetailsUpdateable>,
  ): Promise<void> {
    // In case there is an error, only unimplemented retry should force
    this._queue.push(
      createQueueItem(this.getUserDetails(), {
        name,
        profilePicture,
        completedCounter,
        firstDayOfWeek,
        webConfig,
        myDayConfig,
        myDaySuggestionFilters,
      }),
    );

    if (this._currentUpdatePromise != null) {
      // Flatten next items
      if (this._queue.length > 2 && this._queue[1].status !== "loading") {
        const rest = this._queue.splice(1, this._queue.length - 1);
        const item = flattenQueueToItem(rest, this.getUserDetails());
        if (item !== undefined) this._queue.push(item);
      }

      // We don't won't to do 2 emits in sync for only status change
      this._emitChangeWithSafety();
      await this._currentUpdatePromise;
      return;
    } else if (this.hasError()) {
      // We know there is no running HTTP request. So we minimize the server <-> client communication
      flattenQueue(this._queue, this._userDetails);
    }

    const { promise, resolve, reject } = defer<void>();

    try {
      this._currentUpdatePromise = promise;
      let item: IQueueItem | undefined;

      while ((item = this._queue[0])) {
        item.status = "loading";
        this._emitChangeWithSafety();

        try {
          await this._updateServer(authToken, item.updates);
        } catch (error) {
          item.status = "error";
          throw error;
        }

        this._userDetails = item.result;
        this._queue.shift();
      }

      this._emitChangeWithSafety();
    } catch (error) {
      reject(error);
    } finally {
      this._currentUpdatePromise = null;
    }

    resolve();
    // We need to make we await created promise, so won't be hidden errors
    await promise;
  }

  revert() {
    if (this._queue.length > 0) {
      this._queue.length = 0;
      this._emitChangeWithSafety();
    }
  }

  async retry(authToken: string) {
    // Don't re-try if there is no reason too
    if (this._queue.length === 0) {
      return;
    } else {
      return this.update(authToken, {});
    }
  }

  merge(userDetails: IReadonlyUserDetailsRecord) {
    const prevUserDetails = this._userDetails;
    this._userDetails = produce(this._userDetails, (draft) => UserDetailsRecord.merge(draft, userDetails));
    if (this._userDetails !== prevUserDetails) {
      this._emitChangeWithSafety();
    }
  }
}
